import { Injectable, inject } from '@angular/core';
import { Firestore, Unsubscribe, collection, doc, onSnapshot, query, where } from '@angular/fire/firestore';
import { Functions, httpsCallable } from '@angular/fire/functions';
import {
  AccountAccessMode,
  SupportRequestSurface,
  UpcomingSessionBooking,
} from 'src/app/domain/governance/types/governance.types';
import { GovernanceRecordView, parseGovernanceRecord } from './governance-record';

/** Result of the v4_submitAccountV3Decision callable (server-computed). */
export interface SubmitDecisionResult {
  status: 'ACCEPTED' | 'DECLINED';
  accessMode: AccountAccessMode;
  applied: boolean;
}

/** Result of the v4_requestInfoSession callable. */
export interface RequestInfoSessionResult {
  status: 200 | 400;
  requestId?: string;
  notified?: boolean;
  error?: string;
}

/**
 * Reads the account-scoped V3 governance record and submits Lead
 * Administrator decisions through the authoritative callable.
 *
 * Firestore path: `partners/{accountId}/governance/v3Decision` — read-only
 * for clients; rules deny all client writes (enforcement pending 86e2dbnbn).
 * All writes go through `v4_submitAccountV3Decision`, which validates the
 * caller's Lead Administrator authority server-side.
 */
@Injectable({ providedIn: 'root' })
export class GovernanceService {
  private readonly firestore = inject(Firestore);
  private readonly functions = inject(Functions);

  /** Real-time listener on the account's governance record (null = not in program). */
  subscribeToGovernanceRecord(
    partnerId: string,
    onRecord: (record: GovernanceRecordView | null) => void,
    onError: (error: Error) => void,
  ): Unsubscribe {
    const recordDoc = doc(this.firestore, 'partners', partnerId, 'governance', 'v3Decision');
    return onSnapshot(
      recordDoc,
      snap => onRecord(parseGovernanceRecord(snap.data())),
      error => onError(error),
    );
  }

  /**
   * Submits Accept/Decline for the account. The server is authoritative for
   * both authority and transition validity; the snapshot listener above
   * delivers the resulting state change.
   */
  async submitDecision(
    accountId: string,
    decision: 'ACCEPTED' | 'DECLINED',
    expectedPriorStatus?: string,
  ): Promise<SubmitDecisionResult> {
    const submit = httpsCallable<
      { accountId: string; decision: string; expectedPriorStatus?: string },
      SubmitDecisionResult
    >(this.functions, 'v4_submitAccountV3Decision');
    const result = await submit({
      accountId,
      decision,
      ...(expectedPriorStatus !== undefined ? { expectedPriorStatus } : {}),
    });
    return result.data;
  }

  /**
   * Real-time listener on the account's provider-confirmed session bookings
   * (86e2e3ybv slice 2) — documents under the governance request subcollection
   * that the Calendly webhook stamped `bookingStatus: 'booked'`. Reads are
   * member-scoped by the Firestore rules; this data is adult-staff scheduling
   * info only and is purely informational (never gates access).
   */
  subscribeToBookedSessions(
    partnerId: string,
    onBookings: (bookings: UpcomingSessionBooking[]) => void,
    onError: (error: Error) => void,
  ): Unsubscribe {
    const requests = collection(
      this.firestore,
      'partners',
      partnerId,
      'governance',
      'v3Decision',
      'infoSessionRequests',
    );
    return onSnapshot(
      query(requests, where('bookingStatus', '==', 'booked')),
      snap =>
        onBookings(
          snap.docs
            .map(docSnap => {
              const data = docSnap.data() as Record<string, unknown>;
              const startTime = data['eventStartTime'];
              if (typeof startTime !== 'string' || !startTime) return null;
              return {
                requestId: docSnap.id,
                eventStartTime: startTime,
                eventName: typeof data['eventName'] === 'string' ? (data['eventName'] as string) : null,
                cancelUrl: typeof data['inviteeCancelUrl'] === 'string' ? (data['inviteeCancelUrl'] as string) : null,
                rescheduleUrl:
                  typeof data['inviteeRescheduleUrl'] === 'string' ? (data['inviteeRescheduleUrl'] as string) : null,
              } satisfies UpcomingSessionBooking;
            })
            .filter((booking): booking is UpcomingSessionBooking => booking !== null),
        ),
      error => onError(error),
    );
  }

  /**
   * Persists a support/info-session request for the account and notifies
   * Partner Success (86e2dwhcu). Server-side the write lands in the
   * governance path via Admin SDK only — a request is never a decision and
   * cannot change access state. Throws when the server did not record it,
   * so callers can keep their acknowledgement truthful.
   */
  async requestInfoSession(accountId: string, surface: SupportRequestSurface): Promise<string> {
    const request = httpsCallable<{ accountId: string; surface: SupportRequestSurface }, RequestInfoSessionResult>(
      this.functions,
      'v4_requestInfoSession',
    );
    const result = await request({ accountId, surface });
    if (result.data.status !== 200 || !result.data.requestId) {
      throw new Error(result.data.error ?? 'The session request was not recorded.');
    }
    return result.data.requestId;
  }
}
